/**
 * Navigation utility - Wrapper functions for backward compatibility
 * Maps old UniApp navigation to Vue Router
 */

let routerInstance = null

/**
 * Set router instance
 * @param {Object} router - Vue Router instance
 */
export function setRouter(router) {
  routerInstance = router
}

/**
 * Navigate to a page
 * @param {Object|string} options - Route options or path string
 */
export function navigateTo(options) {
  if (!routerInstance) {
    console.error('Router not initialized. Call setRouter() first.')
    return
  }

  if (typeof options === 'string') {
    routerInstance.push(options).catch(() => {})
    return
  }

  const { url, params, query } = options || {}
  if (!url) {
    console.error('navigateTo: url is required')
    return
  }

  // Convert UniApp path format to Vue Router format
  let path = url
  // Remove leading slash if present, we'll add it back
  if (path.startsWith('/')) {
    path = path.substring(1)
  }
  // Remove .vue extension if present
  path = path.replace(/\.vue$/, '')
  // Ensure path starts with /
  if (!path.startsWith('/')) {
    path = '/' + path
  }

  const routeOptions = {
    path: path,
    query: query || params || {},
  }

  routerInstance.push(routeOptions).catch(() => {})
}

/**
 * Navigate back
 * @param {Object} options - Navigation options (delta for number of pages to go back)
 */
export function navigateBack(options = {}) {
  if (!routerInstance) {
    console.error('Router not initialized. Call setRouter() first.')
    return
  }

  const { delta = 1 } = options
  if (delta === 1) {
    routerInstance.back()
  } else {
    // For multiple steps back, we need to go back in history
    window.history.go(-delta)
  }
}

/**
 * Redirect to a page (replace current route)
 * @param {Object|string} options - Route options or path string
 */
export function redirectTo(options) {
  if (!routerInstance) {
    console.error('Router not initialized. Call setRouter() first.')
    return
  }

  if (typeof options === 'string') {
    routerInstance.replace(options).catch(() => {})
    return
  }

  const { url, params, query } = options || {}
  if (!url) {
    console.error('redirectTo: url is required')
    return
  }

  // Convert UniApp path format to Vue Router format
  let path = url
  // Remove leading slash if present, we'll add it back
  if (path.startsWith('/')) {
    path = path.substring(1)
  }
  // Remove .vue extension if present
  path = path.replace(/\.vue$/, '')
  // Ensure path starts with /
  if (!path.startsWith('/')) {
    path = '/' + path
  }

  const routeOptions = {
    path: path,
    query: query || params || {},
  }

  routerInstance.replace(routeOptions).catch(() => {})
}

/**
 * Switch tab (for tabbar navigation)
 * @param {Object|string} options - Route options or path string
 */
export function switchTab(options) {
  if (!routerInstance) {
    console.error('Router not initialized. Call setRouter() first.')
    return
  }

  if (typeof options === 'string') {
    routerInstance.push(options).catch(() => {})
    return
  }

  const { url } = options || {}
  if (!url) {
    console.error('switchTab: url is required')
    return
  }

  // Convert UniApp path format to Vue Router format
  let path = url
  // Remove leading slash if present, we'll add it back
  if (path.startsWith('/')) {
    path = path.substring(1)
  }
  // Remove .vue extension if present
  path = path.replace(/\.vue$/, '')
  // Ensure path starts with /
  if (!path.startsWith('/')) {
    path = '/' + path
  }

  routerInstance.push(path).catch(() => {})
}

/**
 * Re-launch application (reload page)
 */
export function reLaunch(options) {
  if (typeof options === 'string') {
    window.location.href = options
    return
  }

  const { url } = options || {}
  if (url) {
    window.location.href = url
  } else {
    window.location.reload()
  }
}

// Export as default object for compatibility
export default {
  setRouter,
  navigateTo,
  navigateBack,
  redirectTo,
  switchTab,
  reLaunch,
}

